import * as THREE from 'three';
import { terrainHeight } from './terrain';
import { cloudDensityAt } from './storm';
import type { SkyUniforms } from './scene';

// GOD-RAY SHAFTS: a handful of additive, soft-edged light cylinders standing under nearby CLOUD GAPS,
// tilted down the live sun direction. Gaps are found by sampling the SAME cloud field the sky sheet +
// ground shadows use (storm.ts carries the CPU replica of oxCloud), and each shaft DRIFTS with the
// field so it stays under its gap until the gap closes. No post pass, no render targets: ~5 draw
// calls. Complements the grade's screen-space rays (those need the sun on screen; these live in the
// world, visible from any angle, and land where the SUN POOLS brighten the ground).

const COUNT = 5;
const LEN = 250;                        // ground up to the cloud deck (~240m)
const DRIFT_X = 10.0, DRIFT_Z = 6.25;   // world drift of the cloud field: (0.016, 0.010) / 0.0016 m/s
const UP = new THREE.Vector3(0, 1, 0);

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

interface Shaft { mesh: THREE.Mesh; uI: { value: number }; x: number; z: number; cur: number; target: number; }

export class GodRays {
  readonly group = new THREE.Group();
  private readonly shafts: Shaft[] = [];
  private scanT = 0;
  private readonly dir = new THREE.Vector3(0, 1, 0);

  constructor(sky: SkyUniforms) {
    const geom = new THREE.CylinderGeometry(11, 26, LEN, 10, 1, true); // open-ended soft shaft
    for (let i = 0; i < COUNT; i++) {
      const uI = { value: 0 };
      const mat = new THREE.ShaderMaterial({
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
        uniforms: { uI, uSunColor: sky.uSunColor as unknown as THREE.IUniform },
        vertexShader: `varying vec3 vN; varying vec3 vV; varying vec2 vUv;
          void main(){ vUv = uv; vec4 wp = modelMatrix * vec4(position, 1.0); vN = normalize(mat3(modelMatrix) * normal); vV = normalize(cameraPosition - wp.xyz); gl_Position = projectionMatrix * viewMatrix * wp; }`,
        fragmentShader: `varying vec3 vN; varying vec3 vV; varying vec2 vUv; uniform float uI; uniform vec3 uSunColor;
          void main(){
            float core = pow(abs(dot(normalize(vN), normalize(vV))), 1.8);            // silhouette edges melt away
            float vert = (0.30 + 0.70 * vUv.y) * (1.0 - smoothstep(0.86, 1.0, vUv.y)); // brighter near the cloud, soft at the deck seam
            gl_FragColor = vec4(uSunColor * core * vert * uI, 1.0);
          }`,
      });
      const mesh = new THREE.Mesh(geom, mat);
      mesh.visible = false;
      mesh.renderOrder = 3; // composite over water/foam
      this.group.add(mesh);
      this.shafts.push({ mesh, uI, x: 0, z: 0, cur: 0, target: 0 });
    }
  }

  // Gap quality at ground point (x,z): the sun ray through it must pierce a CLEAR gap, and there must
  // be real cloud around that gap (a shaft with no cloud to break the light reads as a glowing tube).
  private score(x: number, z: number, sunDir: THREE.Vector3, t: number, cover: number): number {
    const off = 240 / Math.max(sunDir.y, 0.25);
    const sx = x + sunDir.x * off, sz = z + sunDir.z * off;
    const gap = clamp01((0.40 - (cloudDensityAt(sx, sz, t) + cover)) / 0.08);
    const nb = (cloudDensityAt(sx + 130, sz, t) + cloudDensityAt(sx - 130, sz, t) + cloudDensityAt(sx, sz + 130, t) + cloudDensityAt(sx, sz - 130, t)) / 4 + cover;
    return gap * clamp01((nb - 0.42) / 0.10);
  }

  private rescan(px: number, pz: number, sunDir: THREE.Vector3, t: number, cover: number, gate: number): void {
    for (const s of this.shafts) {
      const dx = s.x - px, dz = s.z - pz;
      const sc = this.score(s.x, s.z, sunDir, t, cover);
      if (s.target > 0 && dx * dx + dz * dz < 190 * 190 && sc > 0.16) {
        s.target = Math.min(1, sc * 1.6) * gate; // gap still open: track its strength
        continue;
      }
      // hunt a fresh gap in an annulus around the player, keeping shafts apart
      let bx = 0, bz = 0, best = 0;
      for (let i = 0; i < 9; i++) {
        const a = Math.random() * Math.PI * 2, r = 45 + Math.random() * 130;
        const cx = px + Math.cos(a) * r, cz = pz + Math.sin(a) * r;
        let ok = true;
        for (const o of this.shafts) if (o !== s && (o.x - cx) * (o.x - cx) + (o.z - cz) * (o.z - cz) < 70 * 70) { ok = false; break; }
        if (!ok) continue;
        const c = this.score(cx, cz, sunDir, t, cover);
        if (c > best) { best = c; bx = cx; bz = cz; }
      }
      if (best > 0.2) { s.x = bx; s.z = bz; s.cur = 0; s.target = Math.min(1, best * 1.6) * gate; }
      else s.target = 0;
    }
  }

  update(dt: number, px: number, pz: number, sunDir: THREE.Vector3, t: number, cover: number, storm: number): void {
    // Global gate: mid/high sun (dawn+dusk belong to the grade's screen-space rays), SOME cloud about
    // but not a shut sky, and no active rain.
    const gate = clamp01((sunDir.y - 0.22) / 0.20)
      * clamp01((cover + 0.05) / 0.12)
      * (1 - clamp01((cover - 0.28) / 0.25))
      * (1 - clamp01(storm * 1.4));
    this.scanT -= dt;
    if (this.scanT <= 0) { this.scanT = 1.4; this.rescan(px, pz, sunDir, t, cover, gate); }
    this.dir.copy(sunDir).normalize();
    for (const s of this.shafts) {
      s.x += DRIFT_X * dt; s.z += DRIFT_Z * dt;                          // ride the gap as the field drifts
      s.cur += (Math.min(s.target, gate) - s.cur) * Math.min(1, dt * 1.5); // ease in/out (no popping)
      const on = s.cur > 0.005;
      s.mesh.visible = on;
      if (!on) continue;
      // Shafts spawn >=45m out but the cloud drift carries them TOWARD the player; once the camera is
      // inside the additive double-sided cylinder the whole screen reads as a milky HAZE (owner bug).
      // Fade to nothing well before the base radius (26m) can swallow the camera.
      const prox = clamp01((Math.hypot(s.x - px, s.z - pz) - 34) / 26);
      s.uI.value = s.cur * 0.16 * prox;
      s.mesh.quaternion.setFromUnitVectors(UP, this.dir);
      const gy = terrainHeight(s.x, s.z);
      s.mesh.position.set(s.x + this.dir.x * LEN * 0.5, gy - 8 + this.dir.y * LEN * 0.5, s.z + this.dir.z * LEN * 0.5);
    }
  }
}
